// Hand-authored to mirror the RTK Query codegen output for the
// CompanyCustomMetric controller (apps/api/src/controllers/company-custom-metric).
// It intentionally follows the generated `injectEndpoints` style so that a
// future `yarn nx run ui-rtk:codegen` run produces a compatible file. The only
// additions beyond codegen are the cache tags (metric list / values freshness)
// and the FormData body for the CSV preview upload.
import { emptySplitApi as api } from "./emptyApi"
const injectedRtkApi = api.injectEndpoints({
  endpoints: build => ({
    companyCustomMetricControllerListMetrics: build.query<
      CompanyCustomMetricControllerListMetricsApiResponse,
      CompanyCustomMetricControllerListMetricsApiArg
    >({
      query: () => ({ url: `/company-custom-metric` }),
      providesTags: ["CompanyCustomMetric"],
    }),
    companyCustomMetricControllerCreateMetric: build.mutation<
      CompanyCustomMetricControllerCreateMetricApiResponse,
      CompanyCustomMetricControllerCreateMetricApiArg
    >({
      query: queryArg => ({
        url: `/company-custom-metric`,
        method: "POST",
        body: queryArg.body,
      }),
      invalidatesTags: ["CompanyCustomMetric"],
    }),
    companyCustomMetricControllerUpdateMetric: build.mutation<
      CompanyCustomMetricControllerUpdateMetricApiResponse,
      CompanyCustomMetricControllerUpdateMetricApiArg
    >({
      query: queryArg => ({
        url: `/company-custom-metric/${queryArg.id}`,
        method: "PATCH",
        body: queryArg.body,
      }),
      invalidatesTags: ["CompanyCustomMetric"],
    }),
    companyCustomMetricControllerDeleteMetric: build.mutation<
      CompanyCustomMetricControllerDeleteMetricApiResponse,
      CompanyCustomMetricControllerDeleteMetricApiArg
    >({
      query: queryArg => ({
        url: `/company-custom-metric/${queryArg.id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CompanyCustomMetric", "CompanyCustomMetricValues"],
    }),
    companyCustomMetricControllerListAllValues: build.query<
      CompanyCustomMetricControllerListAllValuesApiResponse,
      CompanyCustomMetricControllerListAllValuesApiArg
    >({
      query: queryArg => ({
        url: `/company-custom-metric/values`,
        params: {
          startDate: queryArg.startDate,
          endDate: queryArg.endDate,
        },
      }),
      providesTags: ["CompanyCustomMetricValues"],
    }),
    companyCustomMetricControllerListValues: build.query<
      CompanyCustomMetricControllerListValuesApiResponse,
      CompanyCustomMetricControllerListValuesApiArg
    >({
      query: queryArg => ({
        url: `/company-custom-metric/${queryArg.id}/values`,
        params: {
          startDate: queryArg.startDate,
          endDate: queryArg.endDate,
        },
      }),
      providesTags: ["CompanyCustomMetricValues"],
    }),
    companyCustomMetricControllerUpsertValues: build.mutation<
      CompanyCustomMetricControllerUpsertValuesApiResponse,
      CompanyCustomMetricControllerUpsertValuesApiArg
    >({
      query: queryArg => ({
        url: `/company-custom-metric/${queryArg.id}/values`,
        method: "PUT",
        body: queryArg.body,
      }),
      invalidatesTags: ["CompanyCustomMetricValues"],
    }),
    companyCustomMetricControllerDeleteValue: build.mutation<
      CompanyCustomMetricControllerDeleteValueApiResponse,
      CompanyCustomMetricControllerDeleteValueApiArg
    >({
      query: queryArg => ({
        url: `/company-custom-metric/${queryArg.id}/values/${queryArg.valueId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CompanyCustomMetricValues"],
    }),
    companyCustomMetricControllerPreviewCsv: build.mutation<
      CompanyCustomMetricControllerPreviewCsvApiResponse,
      CompanyCustomMetricControllerPreviewCsvApiArg
    >({
      query: queryArg => {
        const formData = new FormData()
        formData.append("file", queryArg.file)
        return {
          url: `/company-custom-metric/${queryArg.id}/values/csv-preview`,
          method: "POST",
          body: formData,
        }
      },
      // Preview writes nothing — no cache invalidation.
    }),
  }),
  overrideExisting: false,
})
export { injectedRtkApi as generatedApi }

export type CustomMetricFormat = "currency" | "number" | "percent" | "decimal"

/** Vocabulary lives in ~/shared/kpi (typed-custom-metrics.ts); mirrored here as the wire type. */
export type CustomMetricTypeDto =
  | "revenue"
  | "ad_spend"
  | "variable_cost"
  | "fixed_cost"

export type CompanyCustomMetricDto = {
  id: string
  companyId: string
  name: string
  format: CustomMetricFormat
  /** DEPRECATED — mirrored from `type === 'ad_spend'`; read `type` instead. */
  includeInAdSpendTotal: boolean
  /** Semantic type opting values into existing KPIs (frontend-summed); null = untyped. */
  type: CustomMetricTypeDto | null
  /** Revenue channel or ad-spend demand category, scoped to `type`. */
  subtype: string | null
  createdAt: string
  updatedAt: string
}
export type CreateCompanyCustomMetricDto = {
  name: string
  format?: CustomMetricFormat
  type?: CustomMetricTypeDto | null
  subtype?: string | null
}
export type UpdateCompanyCustomMetricDto = {
  name?: string
  format?: CustomMetricFormat
  type?: CustomMetricTypeDto | null
  subtype?: string | null
}
/** Bucket dates are date-only strings ('YYYY-MM-DD', UTC); daily entries have startDate == endDate. */
export type CompanyCustomMetricValueDto = {
  id: string
  metricId: string
  companyId: string
  amount: number
  startDate: string
  endDate: string
}
export type CustomMetricValueEntryDto = {
  startDate: string
  endDate: string
  amount: number
}
export type UpsertCompanyCustomMetricValuesDto = {
  values: CustomMetricValueEntryDto[]
}
export type UpsertCompanyCustomMetricValuesResultDto = {
  created: number
  updated: number
}
export type CustomMetricCsvErrorDto = {
  row?: number
  column?: string
  code: string
  message: string
}
export type CustomMetricCsvStatsDto = {
  totalRows: number
  newCount: number
  overwriteCount: number
}
export type CustomMetricCsvPreviewDto = {
  valid: boolean
  errors: CustomMetricCsvErrorDto[]
  rows: CustomMetricValueEntryDto[]
  stats: CustomMetricCsvStatsDto
}
export type CompanyCustomMetricControllerListMetricsApiResponse =
  /** status 200  */ CompanyCustomMetricDto[]
export type CompanyCustomMetricControllerListMetricsApiArg = void
export type CompanyCustomMetricControllerCreateMetricApiResponse =
  /** status 201  */ CompanyCustomMetricDto
export type CompanyCustomMetricControllerCreateMetricApiArg = {
  body: CreateCompanyCustomMetricDto
}
export type CompanyCustomMetricControllerUpdateMetricApiResponse =
  /** status 200  */ CompanyCustomMetricDto
export type CompanyCustomMetricControllerUpdateMetricApiArg = {
  id: string
  body: UpdateCompanyCustomMetricDto
}
export type CompanyCustomMetricControllerDeleteMetricApiResponse =
  /** status 200  */ { success: boolean }
export type CompanyCustomMetricControllerDeleteMetricApiArg = {
  id: string
}
export type CompanyCustomMetricControllerListAllValuesApiResponse =
  /** status 200  */ CompanyCustomMetricValueDto[]
export type CompanyCustomMetricControllerListAllValuesApiArg = {
  startDate?: string
  endDate?: string
}
export type CompanyCustomMetricControllerListValuesApiResponse =
  /** status 200  */ CompanyCustomMetricValueDto[]
export type CompanyCustomMetricControllerListValuesApiArg = {
  id: string
  startDate?: string
  endDate?: string
}
export type CompanyCustomMetricControllerUpsertValuesApiResponse =
  /** status 200  */ UpsertCompanyCustomMetricValuesResultDto
export type CompanyCustomMetricControllerUpsertValuesApiArg = {
  id: string
  body: UpsertCompanyCustomMetricValuesDto
}
export type CompanyCustomMetricControllerDeleteValueApiResponse =
  /** status 200  */ { success: boolean }
export type CompanyCustomMetricControllerDeleteValueApiArg = {
  id: string
  valueId: string
}
export type CompanyCustomMetricControllerPreviewCsvApiResponse =
  /** status 200  */ CustomMetricCsvPreviewDto
export type CompanyCustomMetricControllerPreviewCsvApiArg = {
  id: string
  file: File
}
export const {
  useCompanyCustomMetricControllerListMetricsQuery,
  useCompanyCustomMetricControllerCreateMetricMutation,
  useCompanyCustomMetricControllerUpdateMetricMutation,
  useCompanyCustomMetricControllerDeleteMetricMutation,
  useCompanyCustomMetricControllerListAllValuesQuery,
  useCompanyCustomMetricControllerListValuesQuery,
  useCompanyCustomMetricControllerUpsertValuesMutation,
  useCompanyCustomMetricControllerDeleteValueMutation,
  useCompanyCustomMetricControllerPreviewCsvMutation,
} = injectedRtkApi
